import React, {useEffect, useRef, useState} from 'react';
import {Box, ClickAwayListener, IconButton, ListItemIcon, MenuItem, Paper, Popper} from '@mui/material';
import axios from 'axios';
import SearchIcon from "@mui/icons-material/Search";
import {alpha, styled} from "@mui/material/styles";
import InputBase from "@mui/material/InputBase";
import LoadingMask from "../components/LoadingMask";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import HistoryIcon from "@mui/icons-material/History";
import ClearIcon from "@mui/icons-material/Clear";
import {useNavigate} from "react-router-dom";

const Search = styled('div')(({theme}) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    marginLeft: 0,
    width: '40%',
}));

const SearchIconWrapper = styled('div')(({theme}) => ({
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({theme}) => ({
    color: 'inherit',
    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1, 1, 0),
        paddingLeft: `calc(1em + ${theme.spacing(4)})`,
        transition: theme.transitions.create('width'),
        width: '100%',
    },
}));

const SearchResults = ({
                           anchorEl,
                           inputRef,
                           performSearch,
                           openDropdown,
                           setOpenDropdown,
                           navigate,
                           searchResults,
                           setSearchResults,
                           handleClickAway,
                           loading
                       }) => {

    const [searchHistory, setSearchHistory] = useState([]);

    const searchItemClick = (item) => {
        setOpenDropdown(false);
        if (item.path.startsWith('http://') || item.path.startsWith('https://')) {
            window.open(item.path, '_blank');
        } else {
            navigate(item.path);
        }
    };

    const loadSearchHistory = async () => {
        const response = await axios.get(`/search/history`);
        setSearchHistory(response.data);
    }

    useEffect(() => {
        if (!openDropdown) {
            setSearchResults([]);
        } else {
            loadSearchHistory();
        }
    }, [openDropdown]);

    const deleteSearchItem = async (e, itemToDelete) => {
        e.stopPropagation();
        await axios.delete('/search/history', {
            data: itemToDelete
        });
        const fList = searchHistory.filter(searchItem => searchItem !== itemToDelete);
        setSearchHistory(fList);
    };

    return (
        <>
            {openDropdown && (
                <Popper open={openDropdown} anchorEl={anchorEl}
                        placement="bottom-start"
                        modifiers={[
                            {
                                name: 'zIndex',
                                enabled: true,
                                options: {
                                    zIndex: 1300,
                                },
                            },
                        ]}
                        style={{zIndex: 1300}}
                >
                    <ClickAwayListener onClickAway={handleClickAway}>
                        <Paper
                            elevation={3}
                            style={{
                                maxHeight: '350px',
                                overflowY: 'auto',
                                minWidth: inputRef.current ? inputRef.current.offsetWidth : 'auto',
                                maxWidth: '500px',
                                width: 'fit-content',
                            }}>
                            <LoadingMask visible={loading}/>
                            {searchResults.length > 0 ?
                                searchResults.map((item) => (
                                    <MenuItem key={item.name} onClick={() => searchItemClick(item)}>
                                        <Box display="flex" flexWrap="wrap" alignItems="center">
                                            <Typography noWrap sx={{marginRight: 1}}>{item.name}</Typography>
                                            {item.group && (
                                                <Typography noWrap variant="body2" color="textSecondary">
                                                    {item.group}
                                                </Typography>
                                            )}
                                        </Box>
                                    </MenuItem>
                                ))
                                :
                                !loading && <Typography align='center' sx={{marginTop: '2px', marginBottom: '2px'}}>
                                    No Results
                                </Typography>
                            }
                            <Divider/>
                            <MenuItem sx={{pointerEvents: 'none'}}>
                                <Typography variant='h6'>Recent Searches</Typography>
                            </MenuItem>
                            {
                                searchHistory.map(historyItem => (
                                    <MenuItem
                                        key={historyItem}
                                        onClick={() => performSearch(historyItem)}
                                        sx={{display: "flex", alignItems: "center", paddingRight: 1}}
                                    >
                                        <Box display="flex" alignItems="center" flexGrow={1}>
                                            <ListItemIcon sx={{minWidth: "32px"}}>
                                                <HistoryIcon/>
                                            </ListItemIcon>
                                            <Typography noWrap>{historyItem}</Typography>
                                        </Box>

                                        <IconButton size="small" sx={{padding: 0}}
                                                    onClick={(e) => deleteSearchItem(e, historyItem)}>
                                            <ClearIcon/>
                                        </IconButton>
                                    </MenuItem>
                                ))
                            }
                        </Paper>
                    </ClickAwayListener>
                </Popper>
            )}
        </>
    );
};

export default function SearchBar() {

    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [openDropdown, setOpenDropdown] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const inputRef = useRef(null);
    const [searchLoading, setSearchLoading] = useState(false);

    const performSearch = async (text) => {
        if (!text.trim()) {
            return;
        }
        const params = new URLSearchParams({
            text: text
        });
        setSearchLoading(true);
        setOpenDropdown(true);
        const response = await axios.get(`/search?${params.toString()}`);
        setSearchLoading(false);
        setSearchResults(response.data);
        setOpenDropdown(true);
    };

    const handleClickAway = () => {
        setOpenDropdown(false);
    };

    const onSearchTextBoxClick = () => {
        setOpenDropdown(true);
        setAnchorEl(inputRef.current);
    };

    const handleInputChange = (event) => {
        setSearchQuery(event.target.value);
        setAnchorEl(inputRef.current);
    };

    useEffect(() => {
        setSearchQuery("");
    }, [navigate]);

    const handleKeyPress = (event) => {
        setAnchorEl(inputRef.current);
        if (event.key === 'Enter') {
            if (openDropdown && searchResults.length === 1) {
                navigate(searchResults[0].path);
                setOpenDropdown(false);
            } else {
                performSearch(searchQuery);
            }
        }
    };

    return (
        <>
            <SearchResults anchorEl={anchorEl} inputRef={inputRef} performSearch={performSearch}
                           openDropdown={openDropdown} setOpenDropdown={setOpenDropdown} navigate={navigate}
                           searchResults={searchResults} setSearchResults={setSearchResults}
                           handleClickAway={handleClickAway} loading={searchLoading}/>
            <Search ref={inputRef}>
                <SearchIconWrapper>
                    <SearchIcon/>
                </SearchIconWrapper>
                <StyledInputBase
                    placeholder="Search…"
                    inputProps={{'aria-label': 'search'}}
                    onKeyPress={handleKeyPress}
                    onChange={handleInputChange}
                    onClick={onSearchTextBoxClick}
                    value={searchQuery}
                    noValidate
                    autoComplete="off"
                    sx={{width: '100%'}}
                />
            </Search>
        </>
    );
}
